import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { createPortal } from 'react-dom'
import { __DEV__ } from '@mote/helpers/constants'
import { usePortalManager } from './PortalManagerProvider'
import { PortalProvider, usePortal } from './PortalProvider'

const PORTAL_CLASSNAME = 'mote-portal'
const PORTAL_SELECTOR = '.mote-portal'

/**
 * Heavily based on Chakra UI Portal component.
 *
 * @see Docs     https://chakra-ui.com/docs/components/other/portal
 * @see Source   https://github.com/chakra-ui/chakra-ui/blob/main/packages/portal/src/portal.tsx
 */

const Container = (
  props: React.PropsWithChildren<{ zIndex: number }>
): JSX.Element => (
  <div
    className='mote-portal-zIndex'
    style={{
      position: 'absolute',
      zIndex: props.zIndex,
      top: 0,
      left: 0,
      right: 0
    }}
  >
    {props.children}
  </div>
)

const DefaultPortal = (
  props: React.PropsWithChildren<{ appendToParentPortal?: boolean }>
): JSX.Element => {
  const { appendToParentPortal, children } = props

  const [tempNode, setTempNode] = useState<HTMLElement | null>(null)
  const portal = useRef<HTMLDivElement | null>(null)

  const [, forceUpdate] = useState({})
  useEffect(() => forceUpdate({}), [])

  const parentPortal = usePortal()
  const manager = usePortalManager()

  useLayoutEffect(() => {
    if (!tempNode) return

    const doc = tempNode.ownerDocument
    const host = appendToParentPortal ? parentPortal ?? doc.body : doc.body

    if (!host) return

    portal.current = doc.createElement('div')
    portal.current.className = PORTAL_CLASSNAME

    host.appendChild(portal.current)
    forceUpdate({})

    const portalNode = portal.current
    return () => {
      if (host.contains(portalNode)) {
        host.removeChild(portalNode)
      }
    }
  }, [tempNode])

  const _children = manager?.zIndex ? (
    <Container zIndex={manager?.zIndex}>{children}</Container>
  ) : (
    children
  )

  return portal.current ? (
    createPortal(
      <PortalProvider value={portal.current}>{_children}</PortalProvider>,
      portal.current
    )
  ) : (
    <span
      ref={el => {
        if (el) setTempNode(el)
      }}
    />
  )
}

interface ContainerPortalProps extends React.PropsWithChildren<{}> {
  containerRef: React.RefObject<HTMLElement | null>
  appendToParentPortal?: boolean
}

const ContainerPortal = (props: ContainerPortalProps): JSX.Element => {
  const { children, containerRef, appendToParentPortal } = props
  const containerEl = containerRef.current
  const host = containerEl ?? document.body

  const portal = useMemo(() => {
    const node = containerEl?.ownerDocument.createElement('div')
    if (node) node.className = PORTAL_CLASSNAME
    return node
  }, [containerEl])

  const [, forceUpdate] = useState({})
  useLayoutEffect(() => forceUpdate({}), [])

  useLayoutEffect(() => {
    if (!portal || !host) return
    host.appendChild(portal)
    return () => {
      host.removeChild(portal)
    }
  }, [portal, host])

  if (host && portal) {
    return createPortal(
      <PortalProvider value={appendToParentPortal ? portal : null}>
        {children}
      </PortalProvider>,
      portal
    )
  }

  return <></>
}

export interface PortalProps {
  containerRef?: React.RefObject<HTMLElement | null>
  children: React.ReactNode
  appendToParentPortal?: boolean
}

export const Portal = (props: PortalProps): JSX.Element => {
  const { containerRef, appendToParentPortal = true, ...rest } = props
  return containerRef ? (
    <ContainerPortal
      containerRef={containerRef}
      appendToParentPortal={appendToParentPortal}
      {...rest}
    />
  ) : (
    <DefaultPortal appendToParentPortal={appendToParentPortal} {...rest} />
  )
}

Portal.className = PORTAL_CLASSNAME
Portal.selector = PORTAL_SELECTOR

if (__DEV__) {
  Portal.displayName = 'Portal'
}
